import { useState, useEffect, useCallback, useRef } from "react";
import VoiceManager from "../services/voiceManager";
import AudioCueManager from "../services/audioCueManager";
import { WebSocketService } from "../services/websocketService";
import {
  AudioSettings,
  AudioDevice,
  ConnectionState,
  AudioTrigger,
  AudioQuality,
} from "../types/audio";
import { RTCSignalData, VoiceStateChangeData } from "../types/websocket";

interface UseVoiceChat {
  isEnabled: boolean;
  isMuted: boolean;
  volume: number;
  settings: AudioSettings;
  availableInputDevices: AudioDevice[];
  availableOutputDevices: AudioDevice[];
  participantStates: Map<
    string,
    { name: string; volume: number; isMuted: boolean; connectionState: ConnectionState }
  >;
  toggleEnabled: () => void;
  toggleMute: () => void;
  setVolume: (_volume: number) => void;
  updateSettings: (_settings: Partial<AudioSettings>) => void;
  setParticipantVolume: (_userId: string, _volume: number) => void;
  triggerAudioCue: (_trigger: AudioTrigger) => void;
  cleanup: () => void;
}

export const useVoiceChat = (
  sessionId: string,
  userId: string,
  websocketService: WebSocketService | null
): UseVoiceChat => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolumeState] = useState(0.7);
  const [settings, setSettings] = useState<AudioSettings>({
    inputDevice: "default",
    outputDevice: "default",
    quality: AudioQuality.MEDIUM,
    noiseSuppression: true,
    echoCancellation: true,
    autoGainControl: true,
    pushToTalk: false,
    pushToTalkKey: "Space",
  });
  const [availableInputDevices, setAvailableInputDevices] = useState<AudioDevice[]>([]);
  const [availableOutputDevices, setAvailableOutputDevices] = useState<AudioDevice[]>([]);
  const [participantStates, setParticipantStates] = useState<
    Map<
      string,
      { name: string; volume: number; isMuted: boolean; connectionState: ConnectionState }
    >
  >(new Map());

  const voiceManagerRef = useRef<VoiceManager | null>(null);
  const audioCueManagerRef = useRef<AudioCueManager | null>(null);
  const remoteAudioElements = useRef<Map<string, HTMLAudioElement>>(new Map());
  // Ref to track enabled state inside async callbacks without stale closures
  const isEnabledRef = useRef(false);

  // Keep ref in sync with state
  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);

  // Initialize managers
  useEffect(() => {
    voiceManagerRef.current = new VoiceManager();
    audioCueManagerRef.current = new AudioCueManager();

    // Initialize audio cue manager
    audioCueManagerRef.current.initialize().catch(console.error);

    const audioElements = remoteAudioElements.current;
    return () => {
      if (voiceManagerRef.current) {
        voiceManagerRef.current.cleanup();
      }
      if (audioCueManagerRef.current) {
        audioCueManagerRef.current.cleanup();
      }
      audioElements.forEach((audio) => {
        audio.pause();
        audio.srcObject = null;
      });
      audioElements.clear();
    };
  }, []);

  // Load available devices
  useEffect(() => {
    const loadDevices = async () => {
      if (voiceManagerRef.current) {
        const devices = await voiceManagerRef.current.getAudioDevices();
        setAvailableInputDevices(devices.inputs);
        setAvailableOutputDevices(devices.outputs);
      }
    };

    loadDevices();

    // Listen for device changes
    navigator.mediaDevices?.addEventListener("devicechange", loadDevices);
    return () => {
      navigator.mediaDevices?.removeEventListener("devicechange", loadDevices);
    };
  }, []);

  // Setup WebSocket message handlers
  useEffect(() => {
    if (!websocketService || !voiceManagerRef.current) return;

    const handleRTCSignal = (data: RTCSignalData) => {
      if (voiceManagerRef.current) {
        voiceManagerRef.current.handleSignal(data).catch(console.error);
      }
    };

    const handleVoiceStateChange = (data: VoiceStateChangeData) => {
      // Update participant mute state
      setParticipantStates((prev) => {
        const newStates = new Map(prev);
        const existing = newStates.get(data.userId);
        if (existing) {
          newStates.set(data.userId, { ...existing, isMuted: data.isMuted });
        } else if (data.isEnabled) {
          // New participant joined voice — add them to state
          newStates.set(data.userId, {
            name: data.userId,
            volume: 1.0,
            isMuted: data.isMuted,
            connectionState: ConnectionState.DISCONNECTED,
          });
        }
        return newStates;
      });

      // Initiate a P2P connection to the new participant if we are already in voice
      if (
        data.isEnabled &&
        data.userId !== userId &&
        isEnabledRef.current &&
        voiceManagerRef.current
      ) {
        const alreadyConnected = voiceManagerRef.current.getConnections().has(data.userId);
        if (!alreadyConnected) {
          voiceManagerRef.current
            .connectToParticipant(data.userId, true)
            .catch((err) => console.error("Failed to connect to voice participant:", err));
        }
      }

      // Clean up when a participant leaves voice
      if (data.isEnabled === false && data.userId !== userId && voiceManagerRef.current) {
        voiceManagerRef.current.disconnectFromParticipant(data.userId);
        const audioEl = remoteAudioElements.current.get(data.userId);
        if (audioEl) {
          audioEl.pause();
          audioEl.srcObject = null;
          remoteAudioElements.current.delete(data.userId);
        }
        setParticipantStates((prev) => {
          const newStates = new Map(prev);
          newStates.delete(data.userId);
          return newStates;
        });
      }
    };

    websocketService.on("rtc_signal", handleRTCSignal);
    websocketService.on("voice_state_change", handleVoiceStateChange);

    return () => {
      websocketService.off("rtc_signal", handleRTCSignal);
      websocketService.off("voice_state_change", handleVoiceStateChange);
    };
  }, [websocketService, userId]);

  // Setup voice manager callbacks
  useEffect(() => {
    if (!voiceManagerRef.current || !websocketService) return;

    // Route RTC signals through the WebSocket to the specific target peer
    voiceManagerRef.current.setSignalCallback((data: RTCSignalData) => {
      websocketService.sendMessage(
        "rtc_signal",
        data,
        [data.targetUserId], // point-to-point: only send to the target
        sessionId
      );
    });

    voiceManagerRef.current.setConnectionStateChangeCallback(
      (peerId: string, state: ConnectionState) => {
        setParticipantStates((prev) => {
          const newStates = new Map(prev);
          const existing = newStates.get(peerId);
          if (existing) {
            newStates.set(peerId, { ...existing, connectionState: state });
          } else {
            newStates.set(peerId, {
              name: peerId,
              volume: 1.0,
              isMuted: false,
              connectionState: state,
            });
          }
          return newStates;
        });
      }
    );

    voiceManagerRef.current.setRemoteStreamCallback((peerId: string, stream: MediaStream) => {
      // Create or reuse an audio element for the remote stream
      let audioElement = remoteAudioElements.current.get(peerId);
      if (!audioElement) {
        audioElement = new Audio();
        audioElement.autoplay = true;
        remoteAudioElements.current.set(peerId, audioElement);
      }
      audioElement.srcObject = stream;
      audioElement.play().catch(console.error);
    });
  }, [websocketService, sessionId]);

  const toggleEnabled = useCallback(async () => {
    if (!voiceManagerRef.current) return;

    if (!isEnabled) {
      try {
        await voiceManagerRef.current.initializeVoiceChat(sessionId, userId);
        setIsEnabled(true);
        isEnabledRef.current = true;
        setIsMuted(voiceManagerRef.current.isMicMuted());

        // Announce to all session participants that we joined voice.
        // Other voice-enabled participants will initiate P2P connections back to us.
        if (websocketService) {
          websocketService.sendMessage(
            "voice_state_change",
            {
              userId,
              isEnabled: true,
              isMuted: voiceManagerRef.current.isMicMuted(),
              isSpeaking: false,
            },
            undefined,
            sessionId
          );
        }
      } catch (error) {
        console.error("Failed to enable voice chat:", error);
        alert("Failed to enable voice chat. Please check your microphone permissions.");
      }
    } else {
      // Announce leaving voice before cleanup
      if (websocketService) {
        websocketService.sendMessage(
          "voice_state_change",
          {
            userId,
            isEnabled: false,
            isMuted: true,
            isSpeaking: false,
          },
          undefined,
          sessionId
        );
      }
      await voiceManagerRef.current.cleanup();
      setIsEnabled(false);
      isEnabledRef.current = false;
      setIsMuted(false);
      setParticipantStates(new Map());
    }
  }, [isEnabled, sessionId, userId, websocketService]);

  const toggleMute = useCallback(() => {
    if (!voiceManagerRef.current || !isEnabled) return;

    const newMuted = !isMuted;
    voiceManagerRef.current.setMuted(newMuted);
    setIsMuted(newMuted);

    // Broadcast mute state change to all participants
    if (websocketService) {
      websocketService.sendMessage(
        "voice_state_change",
        {
          userId,
          isEnabled: true,
          isMuted: newMuted,
          isSpeaking: false,
        },
        undefined,
        sessionId
      );
    }
  }, [isMuted, isEnabled, userId, websocketService, sessionId]);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
    if (audioCueManagerRef.current) {
      audioCueManagerRef.current.setMasterVolume(newVolume);
    }
  }, []);

  const updateSettings = useCallback(async (newSettings: Partial<AudioSettings>) => {
    if (!voiceManagerRef.current) return;

    setSettings((prev) => ({ ...prev, ...newSettings }));
    await voiceManagerRef.current.updateSettings(newSettings);
  }, []);

  const setParticipantVolume = useCallback((participantUserId: string, newVolume: number) => {
    if (!voiceManagerRef.current) return;

    voiceManagerRef.current.setParticipantVolume(participantUserId, newVolume);

    // Update audio element volume
    const audioElement = remoteAudioElements.current.get(participantUserId);
    if (audioElement) {
      audioElement.volume = newVolume;
    }

    setParticipantStates((prev) => {
      const newStates = new Map(prev);
      const existing = newStates.get(participantUserId);
      if (existing) {
        newStates.set(participantUserId, { ...existing, volume: newVolume });
      }
      return newStates;
    });
  }, []);

  const triggerAudioCue = useCallback((trigger: AudioTrigger) => {
    if (audioCueManagerRef.current) {
      audioCueManagerRef.current.triggerCue(trigger);
    }
  }, []);

  const cleanup = useCallback(async () => {
    if (voiceManagerRef.current) {
      await voiceManagerRef.current.cleanup();
    }
    if (audioCueManagerRef.current) {
      await audioCueManagerRef.current.cleanup();
    }
    remoteAudioElements.current.forEach((audio) => {
      audio.pause();
      audio.srcObject = null;
    });
    remoteAudioElements.current.clear();
  }, []);

  return {
    isEnabled,
    isMuted,
    volume,
    settings,
    availableInputDevices,
    availableOutputDevices,
    participantStates,
    toggleEnabled,
    toggleMute,
    setVolume,
    updateSettings,
    setParticipantVolume,
    triggerAudioCue,
    cleanup,
  };
};

export default useVoiceChat;
